import type { AnnotationConnectionResp } from "@/api";
import { parseUtcMs } from "@/utils/datetime";
import {
  FALLBACK_MAX_TRACK_FRAMES,
  judgeChunkDone,
  parseTrackingMessage,
  planNextChunk,
  shouldAcceptEchoFrame,
  TRACKING_ABORTED_MESSAGE,
  trackingErrorMessage,
  type TrackingChunkPlan,
  type TrackingFrameObject,
  type TrackingKeyframeSeed,
} from "./trackingProtocol";

/**
 * 트래킹 직결 WS 드라이버. connection 발급 결과로 청크 루프를 실행하고 frame 스트림을
 * AsyncIterable 로 제공한다(c6 RFC 018 `previewTracking(request, {signal})` 호환).
 *
 * 서버 취소 수단은 close 뿐이다. 그래서 abort 와 조기 종료는 소켓을 닫아 전파한다. abort 는
 * 에러가 아니라 조용한 종료다. 부분 결과를 보존할지는 소비자(컨트롤러)가 정한다.
 * 자동 재시도는 없다. 수동 재시도만 있다(원본 useTrackingStream 과 같다).
 */

export type TrackingRequest = {
  // Tracking 대상 (camera, episode). echo 판정과 connection 발급이 함께 쓴다.
  camera: string;
  episode: string;
  // frame meta 의 num_frames. 0 이면 fallback 상한을 목표로 두고 영상 끝에서 멈춘다.
  numFrames: number;
  // 컨디셔닝 keyframe. connection 요청과 같은 canonical 순서(빌더가 보장)여야
  // 응답 init.keyframes[i] 와 index 로 대응한다.
  keyframes: readonly TrackingKeyframeSeed[];
};

export type TrackingStreamEvent =
  | {
      type: "frame";
      frameIndex: number;
      width: number;
      height: number;
      objects: TrackingFrameObject[];
      jpegBase64: string;
    }
  | { type: "chunk-done"; endFrame: number }
  | { type: "done"; reachedEnd: boolean };

/** 서버가 보낸 에러(busy/track_failed 등)와 비정상 close. message 는 사용자 문구다. */
export class TrackingStreamError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "TrackingStreamError";
  }
}

// 네이티브 WebSocket 을 핸들러 프로퍼티만으로 감싼 좁은 계약. 테스트가 fake 를 주입한다.
export type TrackingSocketLike = {
  send(data: string): void;
  close(code?: number): void;
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number; reason: string }) => void) | null;
  onerror: (() => void) | null;
};

export type TrackingSocketFactory = (url: string) => TrackingSocketLike;

export type TrackingDriverDeps = {
  // connection 발급(재발급 포함). 게이트와 에러 매핑은 api 계층이 소유한다.
  issueConnection: () => Promise<AnnotationConnectionResp>;
  createSocket: TrackingSocketFactory;
  // 만료 판정 시각. 테스트 주입용이다. 기본값은 Date.now.
  now?: () => number;
};

export type TrackingDriver = {
  previewTracking(
    request: TrackingRequest,
    options: { signal: AbortSignal },
  ): AsyncGenerator<TrackingStreamEvent, void>;
};

// 만료 임박 마진. 청크 open 직전 남은 시간이 이보다 적으면 재발급한다.
const CONNECTION_EXPIRY_MARGIN_MS = 60_000;

export function createBrowserTrackingSocketFactory(): TrackingSocketFactory {
  return (url) => {
    const ws = new WebSocket(url);
    const wrapper: TrackingSocketLike = {
      send: (data) => ws.send(data),
      close: (code) => ws.close(code),
      onopen: null,
      onmessage: null,
      onclose: null,
      onerror: null,
    };
    ws.onopen = () => wrapper.onopen?.();
    ws.onmessage = (event) => wrapper.onmessage?.({ data: event.data });
    ws.onclose = (event) => wrapper.onclose?.({ code: event.code, reason: event.reason });
    ws.onerror = () => wrapper.onerror?.();
    return wrapper;
  };
}

export function createTrackingDriver(deps: TrackingDriverDeps): TrackingDriver {
  const now = deps.now ?? Date.now;

  async function* previewTracking(
    request: TrackingRequest,
    options: { signal: AbortSignal },
  ): AsyncGenerator<TrackingStreamEvent, void> {
    const { signal } = options;
    if (signal.aborted) {
      return;
    }

    const targetEnd = request.numFrames > 0 ? request.numFrames : FALLBACK_MAX_TRACK_FRAMES;
    let connection = await deps.issueConnection();
    let chunkStart = 0;

    while (!signal.aborted) {
      const plan = planNextChunk(chunkStart, targetEnd);
      if (plan === null) {
        yield { type: "done", reachedEnd: true };
        return;
      }

      if (isConnectionExpiring(connection, now())) {
        connection = await deps.issueConnection();
      }
      if (signal.aborted) {
        return;
      }

      let doneEndFrame: number | null = null;
      for await (const event of streamChunk(deps.createSocket, connection, request, plan, signal)) {
        if (event.type === "chunk-done") {
          doneEndFrame = event.endFrame;
        }
        yield event;
      }
      if (doneEndFrame === null) {
        // done 없이 스트림이 끝나는 경로는 abort 뿐이다. 다른 경우에는 streamChunk 가 throw 한다.
        return;
      }

      const outcome = judgeChunkDone(plan, doneEndFrame, targetEnd);
      if (outcome.reachedEnd) {
        yield { type: "done", reachedEnd: true };
        return;
      }
      chunkStart = outcome.nextStart;
    }
  }

  return { previewTracking };
}

function isConnectionExpiring(connection: AnnotationConnectionResp, nowMs: number): boolean {
  const expiresMs = parseUtcMs(connection.expires_at);
  // 만료를 읽지 못하면 발급 직후라는 보장이 없다. 그래서 보수적으로 재발급한다.
  return expiresMs === null || expiresMs - nowMs <= CONNECTION_EXPIRY_MARGIN_MS;
}

/**
 * 한 청크의 WS 세션을 frame/chunk-done 이벤트 스트림으로 바꾼다.
 *
 * onmessage 가 큐에 넣고, 소비자는 큐를 비우고 기다린다. 단일 생산자·단일 소비자 브리지다.
 * 종료는 반드시 onclose 에서 판정한다. done 뒤에도 holder 가 close(1000)를 보낸다.
 * abort 는 소켓을 닫고 조용히 끝난다(throw 하지 않는다).
 */
async function* streamChunk(
  createSocket: TrackingSocketFactory,
  connection: AnnotationConnectionResp,
  request: TrackingRequest,
  plan: TrackingChunkPlan,
  signal: AbortSignal,
): AsyncGenerator<Exclude<TrackingStreamEvent, { type: "done" }>, void> {
  const queue: Exclude<TrackingStreamEvent, { type: "done" }>[] = [];
  let wake: (() => void) | null = null;
  let terminalError: Error | null = null;
  let closed = false;
  let sawFirstFrame = false;
  let doneReceived = false;
  let serverError: TrackingStreamError | null = null;

  const notify = () => {
    wake?.();
    wake = null;
  };

  const socket = createSocket(connection.ws_url);
  const onAbort = () => {
    socket.close(1000);
  };
  signal.addEventListener("abort", onAbort, { once: true });

  socket.onopen = () => {
    socket.send(
      JSON.stringify({
        video: connection.init.video,
        frame_index: plan.startFrame,
        objects: [],
        // init.keyframes[i] ↔ request.keyframes[i] 는 발급 시 canonical 순서로 대응한다.
        keyframes: connection.init.keyframes.map((keyframe, index) => ({
          video: keyframe.video,
          frame_index: keyframe.frame_index,
          objects: request.keyframes[index]?.objects ?? [],
        })),
        max_frames: plan.maxFrames,
      }),
    );
  };

  socket.onmessage = (event) => {
    const message = parseTrackingMessage(event.data);
    if (message === null) {
      return;
    }
    if (message.type === "frame") {
      if (!sawFirstFrame) {
        // 청크의 첫 frame 은 컨디셔닝 echo 다. 유효 위치가 아니면 버린다.
        sawFirstFrame = true;
        if (!shouldAcceptEchoFrame(request.keyframes, request, plan.startFrame)) {
          return;
        }
      }
      queue.push({
        type: "frame",
        frameIndex: message.frameIndex,
        width: message.width,
        height: message.height,
        objects: message.objects,
        jpegBase64: message.jpegBase64,
      });
      notify();
    } else if (message.type === "done") {
      doneReceived = true;
      queue.push({ type: "chunk-done", endFrame: message.endFrame });
      notify();
    } else if (message.type === "error") {
      serverError = new TrackingStreamError(
        message.code,
        trackingErrorMessage(message.code, message.message),
      );
    }
  };

  socket.onerror = () => {
    // 브라우저는 핸드셰이크/전송 실패의 상세를 숨긴다. 그래서 판정은 onclose 에서 한다.
  };

  socket.onclose = () => {
    if (closed) {
      return;
    }
    closed = true;
    if (serverError !== null) {
      terminalError = serverError;
    } else if (!doneReceived && !signal.aborted) {
      terminalError = new TrackingStreamError("aborted", TRACKING_ABORTED_MESSAGE);
    }
    notify();
  };

  try {
    while (true) {
      const next = queue.shift();
      if (next !== undefined) {
        yield next;
        continue;
      }
      if (closed || signal.aborted) {
        if (terminalError !== null && !signal.aborted) {
          throw terminalError;
        }
        return;
      }
      await new Promise<void>((resolve) => {
        wake = resolve;
      });
    }
  } finally {
    signal.removeEventListener("abort", onAbort);
    socket.close(1000);
  }
}
